/**
 * Connections page for managing integrations with external marketing
 * platforms. Account-level connectors (admin-managed) are grouped into
 * Connection Type sections, each listing one or more named connection
 * instances. OAuth connectors remain per-user in their own section.
 */
import type { AppRouter } from "@nucleus/api/src/trpc/router";
import type { inferRouterOutputs } from "@trpc/server";
import {
	AlertTriangle,
	CheckCircle2,
	Circle,
	Lock,
	Plus,
	RefreshCw,
	Search,
	XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

interface ConnectorInfo {
	connectorKey: string;
	displayName: string;
	description?: string;
	category: string;
	icon?: string;
	authMethod: "oauth" | "service_account" | "api_credentials";
	apiCredentials?: {
		fields: {
			key: string;
			label: string;
			secret?: boolean;
			placeholder?: string;
			helpText?: string;
		}[];
	};
}

interface OAuthConnection {
	connectorSlug: string;
	status: string;
	lastUsedAt: Date | null;
}

// A single named account-level connection instance (credentials excluded).
type AccountConnection =
	inferRouterOutputs<AppRouter>["integrations"]["listAccountConnections"][number];

// ════════════════════════════════════════════════════════════════════════════
// CATEGORY BADGE
// ════════════════════════════════════════════════════════════════════════════

/** Tailwind color classes for each connector category */
const CATEGORY_BADGE_CLASSES: Record<string, string> = {
	ads: "bg-blue-500/15 text-blue-300 border-blue-400/40",
	dsp: "bg-purple-500/15 text-purple-300 border-purple-400/40",
	analytics: "bg-green-500/15 text-green-300 border-green-400/40",
	data: "bg-orange-500/15 text-orange-300 border-orange-400/40",
	social: "bg-pink-500/15 text-pink-300 border-pink-400/40",
	crm: "bg-indigo-500/15 text-indigo-300 border-indigo-400/40",
};

/** Status dot color + label for each account-connection status value */
const STATUS_META: Record<string, { label: string; dotClass: string }> = {
	active: { label: "Connected", dotClass: "fill-emerald-400 text-emerald-400" },
	error: { label: "Error", dotClass: "fill-red-400 text-red-400" },
	inactive: {
		label: "Not connected",
		dotClass: "fill-muted-foreground/40 text-muted-foreground/40",
	},
};

/**
 * Reads a string-valued field from a connection config object, if present.
 * @param config - The connection config record (may be null/undefined)
 * @param key - The config key to read
 */
function readConfigString(
	config: Record<string, unknown> | null | undefined,
	key: string
): string {
	if (config && typeof config[key] === "string") {
		return config[key] as string;
	}
	return "";
}

// ════════════════════════════════════════════════════════════════════════════
// INLINE NOTIFICATION
// ════════════════════════════════════════════════════════════════════════════

interface Notification {
	kind: "success" | "error";
	title: string;
	message: string;
}

interface NotificationBannerProps {
	notification: Notification;
	onDismiss: () => void;
}

/**
 * Simple inline notification banner for success and error messages.
 * @param props - Component props
 * @param props.notification - The notification to display
 * @param props.onDismiss - Callback to dismiss the notification
 */
function NotificationBanner({ notification, onDismiss }: NotificationBannerProps) {
	const isError = notification.kind === "error";

	return (
		<div
			className={`flex items-start gap-3 rounded-md border p-4 ${
				isError
					? "border-destructive/30 bg-destructive/10"
					: "border-emerald-400/40 bg-emerald-500/10"
			}`}
		>
			{isError
				? <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
				: <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
			}
			<div className="flex-1">
				<p className={`text-sm font-medium ${isError ? "text-red-400" : "text-emerald-300"}`}>
					{notification.title}
				</p>
				<p className={`text-sm ${isError ? "text-red-400" : "text-emerald-300"}`}>
					{notification.message}
				</p>
			</div>
			<button
				onClick={onDismiss}
				className="text-muted-foreground hover:text-foreground"
				aria-label="Dismiss notification"
			>
				<XCircle className="h-4 w-4" />
			</button>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// SERVICE ACCOUNT SETUP FORM
// ════════════════════════════════════════════════════════════════════════════

interface ServiceAccountSetupFormProps {
	connectorSlug: string;
	connection?: AccountConnection | null;
	onSuccess: () => void;
	onNotification: (n: Notification) => void;
}

/**
 * Form for creating or editing a named service-account connection instance.
 * On create the JSON key is required; on edit it is optional (blank keeps the
 * existing key, enabling rename/config edits without re-entry).
 * Used for connectors with authMethod === "service_account".
 * @param props - Component props
 * @param props.connectorSlug - The connector type to configure
 * @param props.connection - The instance being edited, or null/undefined to create
 * @param props.onSuccess - Callback on successful save
 * @param props.onNotification - Callback to show a notification banner
 */
function ServiceAccountSetupForm({
	connectorSlug,
	connection,
	onSuccess,
	onNotification,
}: ServiceAccountSetupFormProps) {
	const isBigQuery = connectorSlug === "bigquery";
	const isDv360 = connectorSlug === "dv360";
	const isEdit = !!connection;
	const [name, setName] = useState(connection?.name ?? "");
	const [jsonContent, setJsonContent] = useState("");
	const [profileId, setProfileId] = useState(readConfigString(connection?.config, "profileId"));
	const [projectId, setProjectId] = useState(readConfigString(connection?.config, "projectId"));
	const [partnerId, setPartnerId] = useState(readConfigString(connection?.config, "partnerId"));
	const [jsonError, setJsonError] = useState<string | null>(null);

	const saveMutation = trpc.integrations.saveAccountCredentials.useMutation({
		onSuccess: () => {
			onSuccess();
			onNotification({
				kind: "success",
				title: "Credentials saved",
				message: "Service account credentials have been saved successfully.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Save failed",
				message: error.message,
			});
		},
	});

	/**
	 * Validates the pasted JSON client-side before submitting.
	 * Server performs the same validation for security.
	 * @param text - The raw JSON text to validate
	 */
	const validateJson = (text: string): boolean => {
		try {
			const parsed = JSON.parse(text) as Record<string, unknown>;
			if (parsed.type !== "service_account") {
				setJsonError("This is not a service account key file (type must be ‘service_account’)");
				return false;
			}
			if (!parsed.client_email || !parsed.private_key) {
				setJsonError("Missing required fields: client_email and private_key");
				return false;
			}
			setJsonError(null);
			return true;
		}
		catch {
			setJsonError("Invalid JSON format");
			return false;
		}
	};

	const fieldsFilled = isDv360
		? !!partnerId.trim()
		: isBigQuery
			? !!projectId.trim()
			: !!profileId.trim();
	const hasJson = jsonContent.trim() !== "";

	/** Submits the form after client-side validation passes */
	const handleSubmit = () => {
		const trimmedName = name.trim();
		if (!trimmedName || !fieldsFilled) return;
		if (!isEdit && !hasJson) return;
		if (hasJson && !validateJson(jsonContent)) return;

		saveMutation.mutate({
			connectorSlug,
			name: trimmedName,
			config: isDv360
				? { partnerId: partnerId.trim() }
				: isBigQuery
					? { projectId: projectId.trim() }
					: { profileId: profileId.trim() },
			...(hasJson ? { serviceAccountJson: jsonContent } : {}),
			...(isEdit && connection ? { connectionId: connection.id } : {}),
		});
	};

	return (
		<div className="space-y-4">
			<div className="space-y-1.5">
				<label className="text-sm font-medium" htmlFor={`name-${connectorSlug}`}>
					Connection Name
				</label>
				<input
					id={`name-${connectorSlug}`}
					type="text"
					className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					placeholder="e.g. BigQuery Client"
					value={name}
					onChange={(e) => { setName(e.target.value); }}
				/>
			</div>

			<div className="space-y-1.5">
				<label className="text-sm font-medium" htmlFor={`json-${connectorSlug}`}>
					Service Account JSON Key
				</label>
				<textarea
					id={`json-${connectorSlug}`}
					className={`w-full min-h-[120px] rounded-md border bg-background px-3 py-2 font-mono text-xs resize-y ${
						jsonError ? "border-destructive" : "border-input"
					}`}
					placeholder="Paste the contents of your service account JSON key file here..."
					value={jsonContent}
					onChange={(e) => {
						setJsonContent(e.target.value);
						if (jsonError) setJsonError(null);
					}}
				/>
				{jsonError && (
					<p className="text-xs text-red-400">{jsonError}</p>
				)}
				{isEdit && (
					<p className="text-xs text-muted-foreground">
						Leave blank to keep the existing key.
					</p>
				)}
			</div>

			{isDv360
				? (
					<div className="space-y-1.5">
						<label className="text-sm font-medium" htmlFor={`partnerId-${connectorSlug}`}>
							DV360 Partner ID
						</label>
						<input
							id={`partnerId-${connectorSlug}`}
							type="text"
							className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
							placeholder="e.g. 123456"
							value={partnerId}
							onChange={(e) => { setPartnerId(e.target.value); }}
						/>
						<p className="text-xs text-muted-foreground">
							The DV360 Partner ID to scope this connection. The service account must
							also be granted a role inside DV360 (Google&apos;s UI) before it can read
							data.
						</p>
					</div>
				)
				: isBigQuery
					? (
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor={`projectId-${connectorSlug}`}>
								GCP Project ID
							</label>
							<input
								id={`projectId-${connectorSlug}`}
								type="text"
								className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								placeholder="e.g. my-gcp-project"
								value={projectId}
								onChange={(e) => { setProjectId(e.target.value); }}
							/>
							<p className="text-xs text-muted-foreground">
								GCP project the service account belongs to. Datasets and tables are chosen
								per use inside each module — not here.
							</p>
						</div>
					)
					: (
						<div className="space-y-1.5">
							<label className="text-sm font-medium" htmlFor={`profile-${connectorSlug}`}>
								CM360 Profile ID
							</label>
							<input
								id={`profile-${connectorSlug}`}
								type="text"
								className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								placeholder="e.g. 1234567"
								value={profileId}
								onChange={(e) => { setProfileId(e.target.value); }}
							/>
						</div>
					)}

			<Button
				size="sm"
				disabled={
					saveMutation.isPending || !name.trim() || !fieldsFilled || (!isEdit && !hasJson)
				}
				onClick={handleSubmit}
			>
				{saveMutation.isPending ? "Saving..." : "Save credentials"}
			</Button>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// API CREDENTIALS SETUP FORM
// ════════════════════════════════════════════════════════════════════════════

interface ApiCredentialsSetupFormProps {
	connector: ConnectorInfo;
	connection?: AccountConnection | null;
	onSuccess: () => void;
	onNotification: (n: Notification) => void;
}

/**
 * Form for creating or editing a named api-credentials connection instance,
 * with inputs driven by the connector manifest's apiCredentials.fields.
 * On create all fields are required; on edit they are optional (blank keeps the
 * existing credentials). The server live-validates credentials on save.
 * @param props - Component props
 * @param props.connector - The connector metadata (including field definitions)
 * @param props.connection - The instance being edited, or null/undefined to create
 * @param props.onSuccess - Callback on successful save
 * @param props.onNotification - Callback to show a notification banner
 */
function ApiCredentialsSetupForm({
	connector,
	connection,
	onSuccess,
	onNotification,
}: ApiCredentialsSetupFormProps) {
	const fields = connector.apiCredentials?.fields ?? [];
	const isEdit = !!connection;
	const [name, setName] = useState(connection?.name ?? "");
	const [values, setValues] = useState<Record<string, string>>({});

	const saveMutation = trpc.integrations.saveAccountApiCredentials.useMutation({
		onSuccess: () => {
			onSuccess();
			onNotification({
				kind: "success",
				title: "Credentials saved",
				message: "Credentials were verified with the provider and saved successfully.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Save failed",
				message: error.message,
			});
		},
	});

	const anyFilled = fields.some((f) => (values[f.key] ?? "").trim() !== "");
	const allFilled = fields.every((f) => (values[f.key] ?? "").trim() !== "");

	/** Submits the credentials; the server verifies them live before saving */
	const handleSubmit = () => {
		const trimmedName = name.trim();
		if (!trimmedName) return;
		if (!isEdit && !allFilled) return;
		if (anyFilled && !allFilled) return;

		const credentials = anyFilled
			? Object.fromEntries(fields.map((f) => [f.key, (values[f.key] ?? "").trim()]))
			: undefined;

		saveMutation.mutate({
			connectorSlug: connector.connectorKey,
			name: trimmedName,
			...(credentials ? { credentials } : {}),
			...(isEdit && connection ? { connectionId: connection.id } : {}),
		});
	};

	return (
		<div className="space-y-4">
			<div className="space-y-1.5">
				<label className="text-sm font-medium" htmlFor={`name-${connector.connectorKey}`}>
					Connection Name
				</label>
				<input
					id={`name-${connector.connectorKey}`}
					type="text"
					className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					placeholder="e.g. BigQuery Client"
					value={name}
					onChange={(e) => { setName(e.target.value); }}
				/>
			</div>

			{fields.map((field) => (
				<div key={field.key} className="space-y-1.5">
					<label
						className="text-sm font-medium"
						htmlFor={`${connector.connectorKey}-${field.key}`}
					>
						{field.label}
					</label>
					<input
						id={`${connector.connectorKey}-${field.key}`}
						type={field.secret ? "password" : "text"}
						autoComplete="off"
						className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
						placeholder={field.placeholder}
						value={values[field.key] ?? ""}
						onChange={(e) => {
							setValues((prev) => ({ ...prev, [field.key]: e.target.value }));
						}}
					/>
					{field.helpText && (
						<p className="text-xs text-muted-foreground">{field.helpText}</p>
					)}
				</div>
			))}

			{isEdit && (
				<p className="text-xs text-muted-foreground">
					Leave the credential fields blank to keep the existing credentials.
				</p>
			)}

			<Button
				size="sm"
				disabled={
					saveMutation.isPending ||
					!name.trim() ||
					(!isEdit && !allFilled) ||
					(anyFilled && !allFilled)
				}
				onClick={handleSubmit}
			>
				{saveMutation.isPending ? "Verifying..." : "Save credentials"}
			</Button>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// ADVERTISER SELECTOR
// ════════════════════════════════════════════════════════════════════════════

interface AdvertiserSelectorProps {
	connectionId: string;
	canManage: boolean;
	onNotification: (n: Notification) => void;
}

/**
 * Searchable checkbox list of CM360 advertisers with save and refresh, scoped
 * to a single connection instance so two instances never share cached state.
 * @param props - Component props
 * @param props.connectionId - The CM360 connection instance to scope calls to
 * @param props.canManage - Whether the current user can manage selections
 * @param props.onNotification - Callback to show notification banners
 */
function AdvertiserSelector({ connectionId, canManage, onNotification }: AdvertiserSelectorProps) {
	const [search, setSearch] = useState("");
	const [localSelection, setLocalSelection] = useState<Set<string>>(new Set());
	const [isDirty, setIsDirty] = useState(false);

	const advertisersQuery = trpc.connectors["cm360-reporting"].listAdvertisers.useQuery({
		connectionId,
	});

	const refreshMutation = trpc.connectors["cm360-reporting"].refreshAdvertisers.useMutation({
		onSuccess: () => {
			void advertisersQuery.refetch();
			onNotification({
				kind: "success",
				title: "Advertisers refreshed",
				message: "Advertiser list has been updated from CM360.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Refresh failed",
				message: error.message,
			});
		},
	});

	const saveMutation = trpc.connectors["cm360-reporting"].saveSelectedAdvertisers.useMutation({
		onSuccess: () => {
			setIsDirty(false);
			void advertisersQuery.refetch();
			onNotification({
				kind: "success",
				title: "Selection saved",
				message: "Advertiser selection has been updated.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Save failed",
				message: error.message,
			});
		},
	});

	// Sync local selection state when data loads
	useEffect(() => {
		if (advertisersQuery.data && !isDirty) {
			const selected = new Set(
				advertisersQuery.data
					.filter((a) => a.isSelected)
					.map((a) => a.externalId),
			);
			setLocalSelection(selected);
		}
	}, [advertisersQuery.data, isDirty]);

	const filtered = useMemo(() => {
		if (!advertisersQuery.data) return [];
		const term = search.toLowerCase();
		return advertisersQuery.data.filter(
			(a) =>
				a.name.toLowerCase().includes(term) ||
				a.externalId.includes(term),
		);
	}, [advertisersQuery.data, search]);

	/**
	 * Toggles an advertiser in or out of the local selection set.
	 * @param externalId - The external ID of the advertiser to toggle
	 */
	const toggleAdvertiser = (externalId: string) => {
		setLocalSelection((prev) => {
			const next = new Set(prev);
			if (next.has(externalId)) {
				next.delete(externalId);
			}
			else {
				next.add(externalId);
			}
			return next;
		});
		setIsDirty(true);
	};

	/** Saves the current advertiser selection to this instance's config */
	const handleSave = () => {
		saveMutation.mutate({
			selectedAdvertiserIds: [...localSelection],
			connectionId,
		});
	};

	if (advertisersQuery.isLoading) {
		return (
			<div className="mt-4 rounded-md border border-dashed p-4 text-center">
				<p className="text-sm text-muted-foreground">Loading advertisers...</p>
			</div>
		);
	}

	// Transient fetch failure — the lister now throws instead of returning [], so an
	// error is surfaced as "temporarily unavailable" with a retry, distinct from a
	// genuinely empty list (which still reads as "No advertisers found" below).
	if (advertisersQuery.isError) {
		return (
			<div className="mt-4 space-y-2">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">Advertisers</h3>
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => { void advertisersQuery.refetch(); }}
						>
							<RefreshCw className="mr-1.5 h-3.5 w-3.5" />
							Retry
						</Button>
					)}
				</div>
				<div className="rounded-md border border-dashed p-4 text-center">
					<p className="text-sm text-muted-foreground">
						Advertisers are temporarily unavailable. Please retry.
					</p>
				</div>
			</div>
		);
	}

	if (!advertisersQuery.data || advertisersQuery.data.length === 0) {
		return (
			<div className="mt-4 space-y-2">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">Advertisers</h3>
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							disabled={refreshMutation.isPending}
							onClick={() => { refreshMutation.mutate({ connectionId }); }}
						>
							<RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
							{refreshMutation.isPending ? "Fetching..." : "Fetch advertisers"}
						</Button>
					)}
				</div>
				<div className="rounded-md border border-dashed p-4 text-center">
					<p className="text-sm text-muted-foreground">
						No advertisers found. Click &quot;Fetch advertisers&quot; to load from CM360.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="mt-4 space-y-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium">
					Advertisers
					<span className="ml-1.5 text-xs text-muted-foreground">
						({localSelection.size} of {advertisersQuery.data.length} selected)
					</span>
				</h3>
				<div className="flex items-center gap-2">
					{canManage && isDirty && (
						<Button
							size="sm"
							disabled={saveMutation.isPending}
							onClick={handleSave}
						>
							{saveMutation.isPending ? "Saving..." : "Save selection"}
						</Button>
					)}
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							disabled={refreshMutation.isPending}
							onClick={() => { refreshMutation.mutate({ connectionId }); }}
						>
							<RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
							Refresh
						</Button>
					)}
				</div>
			</div>

			<div className="relative">
				<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
				<input
					type="text"
					className="w-full rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
					placeholder="Search advertisers..."
					value={search}
					onChange={(e) => { setSearch(e.target.value); }}
				/>
			</div>

			<div className="max-h-64 overflow-y-auto rounded-md border">
				{filtered.map((adv) => (
					<label
						key={adv.externalId}
						className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
					>
						<Checkbox
							checked={localSelection.has(adv.externalId)}
							disabled={!canManage}
							onCheckedChange={() => { toggleAdvertiser(adv.externalId); }}
						/>
						<div className="flex-1 min-w-0">
							<p className="text-sm truncate">{adv.name}</p>
							<p className="text-xs text-muted-foreground">ID: {adv.externalId}</p>
						</div>
						{adv.status && (
							<Badge variant="outline" className="text-xs shrink-0">
								{adv.status}
							</Badge>
						)}
					</label>
				))}
				{filtered.length === 0 && (
					<p className="p-3 text-center text-sm text-muted-foreground">
						No advertisers match your search.
					</p>
				)}
			</div>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// DV360 ADVERTISER SELECTOR
// ════════════════════════════════════════════════════════════════════════════

/**
 * Searchable checkbox list of DV360 advertisers with save and refresh, scoped
 * to a single connection instance so two instances never share cached state.
 * A standalone copy of AdvertiserSelector wired to the connectors.dv360
 * namespace — the CM360 selector is intentionally left untouched.
 * @param props - Component props
 * @param props.connectionId - The DV360 connection instance to scope calls to
 * @param props.canManage - Whether the current user can manage selections
 * @param props.onNotification - Callback to show notification banners
 */
function Dv360AdvertiserSelector({ connectionId, canManage, onNotification }: AdvertiserSelectorProps) {
	const [search, setSearch] = useState("");
	const [localSelection, setLocalSelection] = useState<Set<string>>(new Set());
	const [isDirty, setIsDirty] = useState(false);

	const advertisersQuery = trpc.connectors["dv360"].listAdvertisers.useQuery({
		connectionId,
	});

	const refreshMutation = trpc.connectors["dv360"].refreshAdvertisers.useMutation({
		onSuccess: () => {
			void advertisersQuery.refetch();
			onNotification({
				kind: "success",
				title: "Advertisers refreshed",
				message: "Advertiser list has been updated from DV360.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Refresh failed",
				message: error.message,
			});
		},
	});

	const saveMutation = trpc.connectors["dv360"].saveSelectedAdvertisers.useMutation({
		onSuccess: () => {
			setIsDirty(false);
			void advertisersQuery.refetch();
			onNotification({
				kind: "success",
				title: "Selection saved",
				message: "Advertiser selection has been updated.",
			});
		},
		onError: (error) => {
			onNotification({
				kind: "error",
				title: "Save failed",
				message: error.message,
			});
		},
	});

	// Sync local selection state when data loads
	useEffect(() => {
		if (advertisersQuery.data && !isDirty) {
			const selected = new Set(
				advertisersQuery.data
					.filter((a) => a.isSelected)
					.map((a) => a.externalId),
			);
			setLocalSelection(selected);
		}
	}, [advertisersQuery.data, isDirty]);

	const filtered = useMemo(() => {
		if (!advertisersQuery.data) return [];
		const term = search.toLowerCase();
		return advertisersQuery.data.filter(
			(a) =>
				a.name.toLowerCase().includes(term) ||
				a.externalId.includes(term),
		);
	}, [advertisersQuery.data, search]);

	/**
	 * Toggles an advertiser in or out of the local selection set.
	 * @param externalId - The external ID of the advertiser to toggle
	 */
	const toggleAdvertiser = (externalId: string) => {
		setLocalSelection((prev) => {
			const next = new Set(prev);
			if (next.has(externalId)) {
				next.delete(externalId);
			}
			else {
				next.add(externalId);
			}
			return next;
		});
		setIsDirty(true);
	};

	/** Saves the current advertiser selection to this instance's config */
	const handleSave = () => {
		saveMutation.mutate({
			selectedAdvertiserIds: [...localSelection],
			connectionId,
		});
	};

	if (advertisersQuery.isLoading) {
		return (
			<div className="mt-4 rounded-md border border-dashed p-4 text-center">
				<p className="text-sm text-muted-foreground">Loading advertisers...</p>
			</div>
		);
	}

	// Transient fetch failure — the lister now throws instead of returning [], so an
	// error is surfaced as "temporarily unavailable" with a retry, distinct from a
	// genuinely empty list (which still reads as "No advertisers found" below).
	if (advertisersQuery.isError) {
		return (
			<div className="mt-4 space-y-2">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">Advertisers</h3>
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => { void advertisersQuery.refetch(); }}
						>
							<RefreshCw className="mr-1.5 h-3.5 w-3.5" />
							Retry
						</Button>
					)}
				</div>
				<div className="rounded-md border border-dashed p-4 text-center">
					<p className="text-sm text-muted-foreground">
						Advertisers are temporarily unavailable. Please retry.
					</p>
				</div>
			</div>
		);
	}

	if (!advertisersQuery.data || advertisersQuery.data.length === 0) {
		return (
			<div className="mt-4 space-y-2">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">Advertisers</h3>
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							disabled={refreshMutation.isPending}
							onClick={() => { refreshMutation.mutate({ connectionId }); }}
						>
							<RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
							{refreshMutation.isPending ? "Fetching..." : "Fetch advertisers"}
						</Button>
					)}
				</div>
				<div className="rounded-md border border-dashed p-4 text-center">
					<p className="text-sm text-muted-foreground">
						No advertisers found. Click &quot;Fetch advertisers&quot; to load from DV360.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="mt-4 space-y-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium">
					Advertisers
					<span className="ml-1.5 text-xs text-muted-foreground">
						({localSelection.size} of {advertisersQuery.data.length} selected)
					</span>
				</h3>
				<div className="flex items-center gap-2">
					{canManage && isDirty && (
						<Button
							size="sm"
							disabled={saveMutation.isPending}
							onClick={handleSave}
						>
							{saveMutation.isPending ? "Saving..." : "Save selection"}
						</Button>
					)}
					{canManage && (
						<Button
							variant="outline"
							size="sm"
							disabled={refreshMutation.isPending}
							onClick={() => { refreshMutation.mutate({ connectionId }); }}
						>
							<RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
							Refresh
						</Button>
					)}
				</div>
			</div>

			<div className="relative">
				<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
				<input
					type="text"
					className="w-full rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
					placeholder="Search advertisers..."
					value={search}
					onChange={(e) => { setSearch(e.target.value); }}
				/>
			</div>

			<div className="max-h-64 overflow-y-auto rounded-md border">
				{filtered.map((adv) => (
					<label
						key={adv.externalId}
						className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/50"
					>
						<Checkbox
							checked={localSelection.has(adv.externalId)}
							disabled={!canManage}
							onCheckedChange={() => { toggleAdvertiser(adv.externalId); }}
						/>
						<div className="flex-1 min-w-0">
							<p className="text-sm truncate">{adv.name}</p>
							<p className="text-xs text-muted-foreground">ID: {adv.externalId}</p>
						</div>
						{adv.status && (
							<Badge variant="outline" className="text-xs shrink-0">
								{adv.status}
							</Badge>
						)}
					</label>
				))}
				{filtered.length === 0 && (
					<p className="p-3 text-center text-sm text-muted-foreground">
						No advertisers match your search.
					</p>
				)}
			</div>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// CONNECTION INSTANCE CARD
// ════════════════════════════════════════════════════════════════════════════

interface ConnectionInstanceCardProps {
	connector: ConnectorInfo;
	connection: AccountConnection;
	canManage: boolean;
	onEdit: (connection: AccountConnection) => void;
	onDelete: (connection: AccountConnection) => void;
	onSetDefault: (connectionId: string) => void;
	setDefaultPending: boolean;
	onTest?: (connectionId: string) => void;
	testPending: boolean;
	onNotification: (n: Notification) => void;
}

/**
 * Card for a single named account-level connection instance. Shows the instance
 * name, status, a Default badge, per-instance config summary, and (for managers)
 * Test / Set as Default / Edit / Delete actions. CM360 instances also render
 * their own advertiser selector scoped to this instance.
 * @param props - Component props
 * @param props.connector - The connector type metadata
 * @param props.connection - The connection instance to render
 * @param props.canManage - Whether the current user can manage this connection
 * @param props.onEdit - Callback to open the edit form for this instance
 * @param props.onDelete - Callback to request deletion of this instance
 * @param props.onSetDefault - Callback to make this instance the type default
 * @param props.setDefaultPending - Whether this instance's set-default is in progress
 * @param props.onTest - Callback to live-test this instance, if supported
 * @param props.testPending - Whether this instance's connection test is in progress
 * @param props.onNotification - Callback to show a notification banner
 */
function ConnectionInstanceCard({
	connector,
	connection,
	canManage,
	onEdit,
	onDelete,
	onSetDefault,
	setDefaultPending,
	onTest,
	testPending,
	onNotification,
}: ConnectionInstanceCardProps) {
	const isCm360 = connector.connectorKey === "cm360-reporting";
	const isDv360 = connector.connectorKey === "dv360";
	const isActive = connection.status === "active";
	const status = STATUS_META[connection.status] ?? {
		label: connection.status,
		dotClass: "fill-muted-foreground/40 text-muted-foreground/40",
	};
	const profileId = readConfigString(connection.config, "profileId");
	const projectId = readConfigString(connection.config, "projectId");
	const partnerId = readConfigString(connection.config, "partnerId");

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between gap-2">
					<div className="flex items-center gap-2">
						<CardTitle className="text-base">{connection.name}</CardTitle>
						<Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Account-level connection" />
						{connection.isDefault && (
							<Badge
								variant="outline"
								className="border-emerald-400/40 bg-emerald-500/15 text-emerald-300"
							>
								Default
							</Badge>
						)}
					</div>
				</div>
			</CardHeader>

			<CardContent className="pb-3">
				<div className="flex items-center gap-2">
					<Circle className={`h-2 w-2 ${status.dotClass}`} />
					<span className="text-sm text-muted-foreground">{status.label}</span>
				</div>

				{isCm360 && profileId && (
					<p className="mt-1 text-xs text-muted-foreground">
						Profile ID: {profileId}
					</p>
				)}

				{isDv360 && partnerId && (
					<p className="mt-1 text-xs text-muted-foreground">
						Partner ID: {partnerId}
					</p>
				)}

				{connector.connectorKey === "bigquery" && projectId && (
					<p className="mt-1 text-xs text-muted-foreground">
						Project: {projectId}
					</p>
				)}

				{isCm360 && isActive && (
					<AdvertiserSelector
						connectionId={connection.id}
						canManage={canManage}
						onNotification={onNotification}
					/>
				)}

				{isDv360 && isActive && (
					<Dv360AdvertiserSelector
						connectionId={connection.id}
						canManage={canManage}
						onNotification={onNotification}
					/>
				)}
			</CardContent>

			{canManage && (
				<CardFooter className="gap-2">
					{onTest && (
						<Button
							variant="outline"
							size="sm"
							disabled={testPending}
							onClick={() => { onTest(connection.id); }}
						>
							{testPending ? "Testing..." : "Test connection"}
						</Button>
					)}
					{!connection.isDefault && (
						<Button
							variant="outline"
							size="sm"
							disabled={setDefaultPending}
							onClick={() => { onSetDefault(connection.id); }}
						>
							{setDefaultPending ? "Setting..." : "Set as Default"}
						</Button>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => { onEdit(connection); }}
					>
						Edit
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => { onDelete(connection); }}
					>
						Delete
					</Button>
				</CardFooter>
			)}
		</Card>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// CONNECTION TYPE SECTION
// ════════════════════════════════════════════════════════════════════════════

interface ConnectionTypeSectionProps {
	connector: ConnectorInfo;
	connections: AccountConnection[];
	canManage: boolean;
	onRefetch: () => void;
	onNotification: (n: Notification) => void;
	onDelete: (connection: AccountConnection) => void;
	onSetDefault: (connectionId: string) => void;
	setDefaultPendingId: string | null;
	onTest?: (connectionId: string) => void;
	testPendingId?: string | null;
}

/**
 * A Connection Type section: the connector's display name heading, an Add
 * Connection action, and the list of its configured instance cards (or an
 * empty-state message). Owns the create/edit form dialog for this type.
 * @param props - Component props
 * @param props.connector - The connector type metadata
 * @param props.connections - This type's configured connection instances
 * @param props.canManage - Whether the current user can manage connections
 * @param props.onRefetch - Callback to refresh connection data after a change
 * @param props.onNotification - Callback to show a notification banner
 * @param props.onDelete - Callback to request deletion of an instance
 * @param props.onSetDefault - Callback to make an instance the type default
 * @param props.setDefaultPendingId - Id of the instance whose set-default is pending
 * @param props.onTest - Callback to live-test an instance, if supported
 * @param props.testPendingId - Id of the instance whose connection test is pending
 */
function ConnectionTypeSection({
	connector,
	connections,
	canManage,
	onRefetch,
	onNotification,
	onDelete,
	onSetDefault,
	setDefaultPendingId,
	onTest,
	testPendingId,
}: ConnectionTypeSectionProps) {
	// The instance being edited (null = create mode); form is open when formOpen.
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<AccountConnection | null>(null);
	const isServiceAccount = connector.authMethod === "service_account";

	/** Opens the setup form in create mode for this connection type */
	const openCreate = () => {
		setEditing(null);
		setFormOpen(true);
	};

	/**
	 * Opens the setup form in edit mode, prefilled from the given instance.
	 * @param connection - The instance to edit
	 */
	const openEdit = (connection: AccountConnection) => {
		setEditing(connection);
		setFormOpen(true);
	};

	/** Closes the setup form and refreshes connection data after a save */
	const handleSaved = () => {
		setFormOpen(false);
		setEditing(null);
		onRefetch();
	};

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between gap-2">
				<div>
					<h3 className="text-base font-semibold">{connector.displayName}</h3>
					{connector.description && (
						<p className="text-xs text-muted-foreground">{connector.description}</p>
					)}
				</div>
				{canManage && (
					<Button size="sm" onClick={openCreate}>
						<Plus className="mr-1.5 h-3.5 w-3.5" />
						Add Connection
					</Button>
				)}
			</div>

			{connections.length === 0
				? (
					<div className="rounded-md border border-dashed p-4 text-center">
						<p className="text-sm text-muted-foreground">No connections configured.</p>
					</div>
				)
				: (
					<div className="grid grid-cols-1 gap-4">
						{connections.map((connection) => (
							<ConnectionInstanceCard
								key={connection.id}
								connector={connector}
								connection={connection}
								canManage={canManage}
								onEdit={openEdit}
								onDelete={onDelete}
								onSetDefault={onSetDefault}
								setDefaultPending={setDefaultPendingId === connection.id}
								onTest={onTest}
								testPending={testPendingId === connection.id}
								onNotification={onNotification}
							/>
						))}
					</div>
				)}

			<Dialog
				open={formOpen}
				onOpenChange={(isOpen) => {
					if (!isOpen) {
						setFormOpen(false);
						setEditing(null);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editing ? `Edit ${editing.name}` : `Add ${connector.displayName} Connection`}
						</DialogTitle>
						<DialogDescription>
							{editing
								? "Update this connection's name, configuration, or credentials."
								: "Create a new named connection instance for this connection type."}
						</DialogDescription>
					</DialogHeader>
					{isServiceAccount
						? (
							<ServiceAccountSetupForm
								connectorSlug={connector.connectorKey}
								connection={editing}
								onSuccess={handleSaved}
								onNotification={onNotification}
							/>
						)
						: (
							<ApiCredentialsSetupForm
								connector={connector}
								connection={editing}
								onSuccess={handleSaved}
								onNotification={onNotification}
							/>
						)}
				</DialogContent>
			</Dialog>
		</div>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// OAUTH CONNECTOR CARD
// ════════════════════════════════════════════════════════════════════════════

interface OAuthCardProps {
	connector: ConnectorInfo;
	connection: OAuthConnection | undefined;
	isConnecting: boolean;
	onConnect: (connectorSlug: string) => void;
	onDisconnect: (connectorSlug: string) => void;
}

/**
 * Card for OAuth connectors. Shows Connect button (OAuth redirect) or
 * Disconnect button based on current connection status.
 * @param props - Component props
 * @param props.connector - The connector metadata
 * @param props.connection - The user’s OAuth connection, if any
 * @param props.isConnecting - Whether a connect action is in progress
 * @param props.onConnect - Callback to initiate OAuth connection
 * @param props.onDisconnect - Callback to initiate disconnection
 */
function OAuthCard({
	connector,
	connection,
	isConnecting,
	onConnect,
	onDisconnect,
}: OAuthCardProps) {
	const isConnected = !!connection && connection.status === "active";
	const badgeClass = Object.hasOwn(CATEGORY_BADGE_CLASSES, connector.category)
		? CATEGORY_BADGE_CLASSES[connector.category]
		: "bg-muted text-muted-foreground border-border";

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between gap-2">
					<CardTitle className="text-base">{connector.displayName}</CardTitle>
					<Badge className={badgeClass} variant="outline">
						{connector.category}
					</Badge>
				</div>
				{connector.description && (
					<p className="text-xs text-muted-foreground">{connector.description}</p>
				)}
			</CardHeader>

			<CardContent className="pb-3">
				<div className="flex items-center gap-2">
					{isConnected
						? (
							<>
								<Circle className="h-2 w-2 fill-emerald-400 text-emerald-400" />
								<span className="text-sm text-muted-foreground">Connected</span>
							</>
						)
						: (
							<>
								<Circle className="h-2 w-2 fill-muted-foreground/40 text-muted-foreground/40" />
								<span className="text-sm text-muted-foreground">Not connected</span>
							</>
						)
					}
				</div>

				{isConnected && connection.lastUsedAt && (
					<p className="mt-1 text-xs text-muted-foreground">
						Last used: {new Date(connection.lastUsedAt).toLocaleDateString()}
					</p>
				)}
			</CardContent>

			<CardFooter>
				{isConnected
					? (
						<Button
							variant="outline"
							size="sm"
							onClick={() => { onDisconnect(connector.connectorKey); }}
						>
							Disconnect
						</Button>
					)
					: (
						<Button
							size="sm"
							disabled={isConnecting}
							onClick={() => { onConnect(connector.connectorKey); }}
						>
							{isConnecting ? "Redirecting..." : "Connect"}
						</Button>
					)
				}
			</CardFooter>
		</Card>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// SKELETON CARD
// ════════════════════════════════════════════════════════════════════════════

/**
 * Animated skeleton placeholder card shown while connectors are loading.
 */
function SkeletonCard() {
	return (
		<Card className="animate-pulse">
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between gap-2">
					<div className="h-5 w-36 rounded bg-muted" />
					<div className="h-5 w-16 rounded bg-muted" />
				</div>
			</CardHeader>
			<CardContent className="pb-3">
				<div className="h-4 w-24 rounded bg-muted" />
			</CardContent>
			<CardFooter>
				<div className="h-8 w-20 rounded bg-muted" />
			</CardFooter>
		</Card>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// OAUTH DISCONNECT CONFIRMATION DIALOG
// ════════════════════════════════════════════════════════════════════════════

interface DisconnectDialogProps {
	open: boolean;
	isPending: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Confirmation dialog shown before disconnecting a personal OAuth integration.
 * @param props - Component props
 * @param props.open - Whether the dialog is open
 * @param props.isPending - Whether the disconnect mutation is in progress
 * @param props.onConfirm - Callback to confirm the disconnection
 * @param props.onCancel - Callback to cancel and close the dialog
 */
function DisconnectDialog({ open, isPending, onConfirm, onCancel }: DisconnectDialogProps) {
	return (
		<Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { onCancel(); } }}>
			<DialogContent>
				<form onSubmit={(e) => { e.preventDefault(); onConfirm(); }}>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-red-400" />
							Disconnect integration?
						</DialogTitle>
						<DialogDescription>
							This will revoke your personal connection to this service. Any automations
							using this connection will stop working until you reconnect.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={onCancel}
							disabled={isPending}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="destructive"
							disabled={isPending}
						>
							{isPending ? "Disconnecting..." : "Disconnect"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// DELETE CONNECTION CONFIRMATION DIALOG
// ════════════════════════════════════════════════════════════════════════════

interface DeleteConnectionDialogProps {
	connection: AccountConnection | null;
	isPending: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Confirmation dialog shown before deleting an account-level connection
 * instance, identifying it by its user-facing name.
 * @param props - Component props
 * @param props.connection - The instance pending deletion, or null when closed
 * @param props.isPending - Whether the delete mutation is in progress
 * @param props.onConfirm - Callback to confirm the deletion
 * @param props.onCancel - Callback to cancel and close the dialog
 */
function DeleteConnectionDialog({
	connection,
	isPending,
	onConfirm,
	onCancel,
}: DeleteConnectionDialogProps) {
	return (
		<Dialog open={!!connection} onOpenChange={(isOpen) => { if (!isOpen) { onCancel(); } }}>
			<DialogContent>
				<form onSubmit={(e) => { e.preventDefault(); onConfirm(); }}>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-red-400" />
							Delete connection?
						</DialogTitle>
						<DialogDescription>
							This will remove the connection
							{connection ? ` “${connection.name}”` : ""} and its stored
							credentials. All users on this account will lose access through this
							connection.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={onCancel}
							disabled={isPending}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="destructive"
							disabled={isPending}
						>
							{isPending ? "Deleting..." : "Delete"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

// ════════════════════════════════════════════════════════════════════════════
// OAUTH CALLBACK ERROR MESSAGES
// ════════════════════════════════════════════════════════════════════════════

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
	access_denied: "You cancelled the authorization. No connection was made.",
	invalid_state: "The authorization session expired. Please try again.",
	token_exchange_failed: "Failed to complete the connection. Please try again.",
	missing_code: "Authorization response was incomplete. Please try again.",
	storage_failed: "Connection was authorized but could not be saved. Please try again.",
};

// ════════════════════════════════════════════════════════════════════════════
// CONNECTIONS PAGE
// ════════════════════════════════════════════════════════════════════════════

/**
 * Full-page Connections management UI. Account-level connectors are grouped into
 * Connection Type sections, each listing its named instances with per-instance
 * actions; OAuth connectors are per-user in a separate section.
 */
export function ConnectionsPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [notification, setNotification] = useState<Notification | null>(null);

	// Slug of the OAuth connector currently awaiting disconnect confirmation
	const [disconnectSlug, setDisconnectSlug] = useState<string | null>(null);
	// Account-level connection instance pending delete confirmation
	const [deleteTarget, setDeleteTarget] = useState<AccountConnection | null>(null);

	// Slug of the connector currently initiating a Connect (OAuth redirect)
	const [connectingSlug, setConnectingSlug] = useState<string | null>(null);

	const session = useAuthStore((s) => s.session);
	const capabilities = session?.capabilities ?? [];
	const canManage = capabilities.includes("integrations.manage") || session?.role === "super_admin";

	// ── Data queries ──────────────────────────────────────────────────────
	const connectorsQuery = trpc.integrations.listConnectors.useQuery();
	const connectionsQuery = trpc.integrations.listUserConnections.useQuery();
	const accountConnectionsQuery = trpc.integrations.listAccountConnections.useQuery();

	const trpcUtils = trpc.useUtils();

	/** Refreshes the account-level connection list after a mutation */
	const refetchAccountConnections = () => {
		void trpcUtils.integrations.listAccountConnections.invalidate();
	};

	const testDomoMutation = trpc.connectors.domo.testConnection.useMutation({
		onSuccess: () => {
			setNotification({
				kind: "success",
				title: "Connection verified",
				message: "Domo accepted the stored credentials.",
			});
		},
		onError: (error) => {
			setNotification({
				kind: "error",
				title: "Connection test failed",
				message: error.message,
			});
		},
	});

	const testBigqueryMutation = trpc.connectors.bigquery.testConnection.useMutation({
		onSuccess: () => {
			setNotification({
				kind: "success",
				title: "Connection verified",
				message: "BigQuery accepted the service account for this project.",
			});
		},
		onError: (error) => {
			setNotification({
				kind: "error",
				title: "Connection test failed",
				message: error.message,
			});
		},
	});

	const setDefaultMutation = trpc.integrations.setDefaultConnection.useMutation({
		onSuccess: () => {
			refetchAccountConnections();
			setNotification({
				kind: "success",
				title: "Default updated",
				message: "The default connection for this type has been updated.",
			});
		},
		onError: (error) => {
			setNotification({
				kind: "error",
				title: "Update failed",
				message: error.message,
			});
		},
	});

	const deleteConnectionMutation = trpc.integrations.deleteConnection.useMutation({
		onSuccess: () => {
			refetchAccountConnections();
			setNotification({
				kind: "success",
				title: "Connection deleted",
				message: "The connection has been deleted.",
			});
		},
		onError: (error) => {
			setNotification({
				kind: "error",
				title: "Delete failed",
				message: error.message,
			});
		},
	});

	const disconnectMutation = trpc.integrations.disconnect.useMutation({
		onSuccess: () => {
			void connectionsQuery.refetch();
			setNotification({
				kind: "success",
				title: "Disconnected",
				message: "The integration has been disconnected successfully.",
			});
		},
		onError: (error) => {
			setNotification({
				kind: "error",
				title: "Disconnect failed",
				message: error.message,
			});
		},
	});

	// ── OAuth callback URL params ─────────────────────────────────────────
	useEffect(() => {
		const connected = searchParams.get("connected");
		const error = searchParams.get("error");

		if (connected || error) {
			if (connected) {
				const displayName = connected
					.replace(/_/g, " ")
					.replace(/\b\w/g, (c) => c.toUpperCase());
				setNotification({
					kind: "success",
					title: "Connection established",
					message: `Successfully connected to ${displayName}`,
				});
			}

			if (error) {
				setNotification({
					kind: "error",
					title: "Connection failed",
					message: OAUTH_ERROR_MESSAGES[error] ?? `An error occurred: ${error}`,
				});
			}

			// Only remove OAuth-specific params, preserve any others
			const nextParams = new URLSearchParams(searchParams);
			nextParams.delete("connected");
			nextParams.delete("error");
			setSearchParams(nextParams, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	// ── Connect handler (OAuth) ────────────────────────────────────────────
	/**
	 * Initiates an OAuth connection flow by fetching the OAuth URL
	 * and redirecting the browser to the provider’s authorization page.
	 * @param connectorSlug - The connector to connect to
	 */
	const handleConnect = async (connectorSlug: string) => {
		setConnectingSlug(connectorSlug);
		try {
			const result = await trpcUtils.integrations.getOAuthUrl.fetch({ connectorSlug });
			if (result.url) {
				window.location.href = result.url;
			}
		}
		catch (err) {
			const message = err instanceof Error ? err.message : "Failed to start connection";
			setNotification({
				kind: "error",
				title: "Connection failed",
				message,
			});
			setConnectingSlug(null);
		}
	};

	// ── OAuth disconnect handler ───────────────────────────────────────────
	/**
	 * Opens the disconnect confirmation dialog for the given OAuth connector.
	 * @param connectorSlug - The connector to disconnect from
	 */
	const handleDisconnect = (connectorSlug: string) => {
		setDisconnectSlug(connectorSlug);
	};

	/** Executes the OAuth disconnect mutation after the user confirms */
	const confirmDisconnect = () => {
		if (!disconnectSlug) return;
		disconnectMutation.mutate({ connectorSlug: disconnectSlug });
		setDisconnectSlug(null);
	};

	// ── Instance delete handler ────────────────────────────────────────────
	/**
	 * Opens the delete confirmation dialog for a connection instance.
	 * @param connection - The instance to delete
	 */
	const handleRequestDelete = (connection: AccountConnection) => {
		setDeleteTarget(connection);
	};

	/** Executes the delete mutation after the user confirms */
	const confirmDelete = () => {
		if (!deleteTarget) return;
		deleteConnectionMutation.mutate({ connectionId: deleteTarget.id });
		setDeleteTarget(null);
	};

	// ── Set-default handler ────────────────────────────────────────────────
	/**
	 * Makes the given connection instance the default for its type.
	 * @param connectionId - The instance to promote to default
	 */
	const handleSetDefault = (connectionId: string) => {
		setDefaultMutation.mutate({ connectionId });
	};

	// ── Partition connectors and group instances by type ───────────────────
	const allConnectors = connectorsQuery.data ?? [];
	const accountConnectors = allConnectors.filter(
		(c) => c.authMethod === "service_account" || c.authMethod === "api_credentials"
	);
	const oauthConnectors = allConnectors.filter((c) => c.authMethod === "oauth");

	const connectionsBySlug = useMemo(() => {
		const grouped = new Map<string, AccountConnection[]>();
		for (const conn of accountConnectionsQuery.data ?? []) {
			const list = grouped.get(conn.connectorSlug) ?? [];
			list.push(conn);
			grouped.set(conn.connectorSlug, list);
		}
		return grouped;
	}, [accountConnectionsQuery.data]);

	const isLoading = connectorsQuery.isLoading || accountConnectionsQuery.isLoading;

	// Which instance (if any) currently has a test in flight, per connector type
	const bigqueryTestingId = testBigqueryMutation.isPending
		? testBigqueryMutation.variables?.connectionId ?? null
		: null;
	const domoTestingId = testDomoMutation.isPending
		? testDomoMutation.variables?.connectionId ?? null
		: null;
	const setDefaultPendingId = setDefaultMutation.isPending
		? setDefaultMutation.variables?.connectionId ?? null
		: null;

	// ── Render ────────────────────────────────────────────────────────────
	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold">Connections</h1>
				<p className="text-muted-foreground">
					Manage connections to external marketing platforms and data sources
				</p>
			</div>

			{/* Inline notifications */}
			{notification && (
				<NotificationBanner
					notification={notification}
					onDismiss={() => { setNotification(null); }}
				/>
			)}

			{/* Error state */}
			{connectorsQuery.isError && (
				<div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-4">
					<XCircle className="h-4 w-4 shrink-0 text-red-400" />
					<div className="flex-1">
						<p className="text-sm font-medium text-red-400">Failed to load connectors</p>
						<p className="text-sm text-red-400">{connectorsQuery.error.message}</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={() => { void connectorsQuery.refetch(); }}
					>
						Retry
					</Button>
				</div>
			)}

			{/* Loading state */}
			{isLoading && (
				<div className="grid grid-cols-1 gap-4">
					{[1, 2, 3, 4].map((i) => (
						<SkeletonCard key={i} />
					))}
				</div>
			)}

			{/* Empty state */}
			{!isLoading && !connectorsQuery.isError && allConnectors.length === 0 && (
				<div className="rounded-md border border-dashed p-8 text-center">
					<p className="text-sm text-muted-foreground">
						No connectors are available. Contact your administrator.
					</p>
				</div>
			)}

			{/* Account Connections section (service account + api credentials) */}
			{!isLoading && accountConnectors.length > 0 && (
				<div className="space-y-6">
					<div>
						<h2 className="text-lg font-semibold">Account Connections</h2>
						<p className="text-sm text-muted-foreground">
							Shared credentials managed by account administrators
						</p>
					</div>
					{accountConnectors.map((connector) => {
						const isBigQuery = connector.connectorKey === "bigquery";
						const isDomo = connector.connectorKey === "domo";
						const onTest = isBigQuery
							? (connectionId: string) => { testBigqueryMutation.mutate({ connectionId }); }
							: isDomo
								? (connectionId: string) => { testDomoMutation.mutate({ connectionId }); }
								: undefined;
						const testPendingId = isBigQuery
							? bigqueryTestingId
							: isDomo
								? domoTestingId
								: null;

						return (
							<ConnectionTypeSection
								key={connector.connectorKey}
								connector={connector}
								connections={connectionsBySlug.get(connector.connectorKey) ?? []}
								canManage={canManage}
								onRefetch={refetchAccountConnections}
								onNotification={setNotification}
								onDelete={handleRequestDelete}
								onSetDefault={handleSetDefault}
								setDefaultPendingId={setDefaultPendingId}
								onTest={onTest}
								testPendingId={testPendingId}
							/>
						);
					})}
				</div>
			)}

			{/* Your Connections section (OAuth) */}
			{!isLoading && oauthConnectors.length > 0 && (
				<div className="space-y-3">
					<div>
						<h2 className="text-lg font-semibold">Your Connections</h2>
						<p className="text-sm text-muted-foreground">
							Personal connections scoped to your account
						</p>
					</div>
					<div className="grid grid-cols-1 gap-4">
						{oauthConnectors.map((connector) => {
							const connection = connectionsQuery.data?.find(
								(c) => c.connectorSlug === connector.connectorKey
							);

							return (
								<OAuthCard
									key={connector.connectorKey}
									connector={connector}
									connection={connection}
									isConnecting={connectingSlug === connector.connectorKey}
									onConnect={handleConnect}
									onDisconnect={handleDisconnect}
								/>
							);
						})}
					</div>
				</div>
			)}

			{/* OAuth disconnect confirmation dialog */}
			<DisconnectDialog
				open={!!disconnectSlug}
				isPending={disconnectMutation.isPending}
				onConfirm={confirmDisconnect}
				onCancel={() => { setDisconnectSlug(null); }}
			/>

			{/* Instance delete confirmation dialog */}
			<DeleteConnectionDialog
				connection={deleteTarget}
				isPending={deleteConnectionMutation.isPending}
				onConfirm={confirmDelete}
				onCancel={() => { setDeleteTarget(null); }}
			/>
		</div>
	);
}
